/**
 * Resolve the API base URL for the public embeds endpoint.
 *
 * Precedence:
 *   1. `window.ZIPPER_EMBED_API_BASE` (explicit host-page override, e.g. the
 *      admin preview iframe pointing at a branch/staging API)
 *   2. the API that matches the CDN host this bundle was served from — so the
 *      bundle on `cdn-staging.joinzipper.com` talks to staging and the one on
 *      `cdn.joinzipper.com` talks to production, automatically
 *   3. production default (covers self-hosted / unknown hosts)
 *
 * NOTE: these are the API Gateway invoke URLs the platform actually serves on
 * (the same values the web app uses for API_ORIGIN). There is no
 * `api.joinzipper.com` custom domain provisioned — pointing at one silently
 * fails DNS and the embed falls back to mock data. If a stable custom API
 * domain is ever set up, swap these for it (and update the tests).
 */
declare global {
  interface Window {
    ZIPPER_EMBED_API_BASE?: string;
  }
}

import { ScopedSessionStore } from "../state/session-store";

const PROD_API_BASE = 'https://u81l0q8368.execute-api.us-east-1.amazonaws.com';
const STAGING_API_BASE =
  'https://scp5qvvmik.execute-api.us-east-1.amazonaws.com';
const DEFAULT_API_BASE = PROD_API_BASE;

/** Map a known CDN host to its environment's public API base. */
const CDN_HOST_TO_API_BASE: Record<string, string> = {
  'cdn.joinzipper.com': PROD_API_BASE,
  'cdn-staging.joinzipper.com': STAGING_API_BASE,
};

/**
 * Per-branch dev/preview environments use the flattened single-label scheme
 * `{sitekey__}{branchToken}__{role}.joinzipper.{dev|localhost}`. When the
 * bundle is served from a branch's own web host it must use that branch's API;
 * otherwise getApiBase() falls through to production. The localhost variant
 * is the laptop Caddy scheme, not an arbitrary website suffix.
 */
const BRANCH_HOST_RE =
  /^(?:[a-z0-9_-]+__)*?([a-z0-9-]+)__(?:app|sites)\.joinzipper\.(dev|localhost)$/i;

/**
 * The API base for a CDN host, or undefined if the host isn't a known Zipper
 * CDN or branch host. Exported for unit testing the host→API mapping.
 */
export function apiBaseForCdnHost(host: string): string | undefined {
  const known = CDN_HOST_TO_API_BASE[host];
  if (known) return known;
  const branch = BRANCH_HOST_RE.exec(host);
  return branch
    ? `https://${branch[1]}__api.joinzipper.${branch[2]}`
    : undefined;
}

/** API base inferred from where this ES module was loaded (its own URL). */
function hostDerivedApiBase(): string | undefined {
  try {
    return apiBaseForCdnHost(new URL(import.meta.url).hostname);
  } catch {
    return undefined; // non-URL module id (tests / unusual bundlers)
  }
}

export function getApiBase(): string {
  if (typeof window !== 'undefined' && window.ZIPPER_EMBED_API_BASE) {
    return window.ZIPPER_EMBED_API_BASE.replace(/\/$/, '');
  }
  return hostDerivedApiBase() ?? DEFAULT_API_BASE;
}

// ── Organization/audience-scoped customer sessions ──
let sessionStore: ScopedSessionStore | undefined;

function browserSessionStore(): ScopedSessionStore {
  if (!sessionStore) {
    let storage: Storage | undefined;
    try {
      storage = typeof localStorage === "undefined" ? undefined : localStorage;
    } catch {
      storage = undefined;
    }
    sessionStore = new ScopedSessionStore(storage);
  }
  return sessionStore;
}

const publicScope = (orgId: number) => ({
  orgId,
  audience: "public-embed" as const,
});

export function getEmbedToken(orgId: number): string | null {
  return browserSessionStore().read(publicScope(orgId))?.token ?? null;
}

export function setEmbedToken(orgId: number, token: string): void {
  browserSessionStore().write({ scope: publicScope(orgId), token });
}

export function clearEmbedToken(orgId: number): void {
  browserSessionStore().clear(publicScope(orgId));
}

/** Authorization header for the customer session, when signed in. */
export function authHeaders(orgId: number): Record<string, string> {
  const t = getEmbedToken(orgId);
  return t ? { Authorization: `Bearer ${t}` } : {};
}
